import React from 'react';
import { commercialRefinanceQuestionsByName } from 'app/models/questions/commercial-refinance';
import { Section } from 'app/models/types';
import { FieldNames } from 'app/models/fields/names';
import { FormTerms } from 'app/components/FormAgreements/FormTerms';
import {
  isEmployee,
  isEmploymentOther,
  isExistingCommercial,
  isHomeowner,
  isRenting,
  isRetired,
  isSelfEmployed,
} from 'app/models/fields/conditionals';
import { getCommercialDeclarationQuestions } from '../questions/declarations';
import { ConfirmationCommercial } from '../../components/ConfirmationCommercial';
import {
  COMMERCIAL_REFINANCE_LOAN_PURPOSE,
  COMMERCIAL_REFINANCE_PROPERTY_INFO,
  COMMERCIAL_REFINANCE_PROPERTY_COST,
  COMMERCIAL_REFINANCE_BUSINESS_INFO,
  COMMERCIAL_REFINANCE_HOUSING_INFO,
  COMMERCIAL_REFINANCE_LOAN_HOLDER,
  COMMERCIAL_REFINANCE_BORROWER_INCOME_SOURCE,
  COMMERCIAL_REFINANCE_BORROWER_EMPLOYMENT,
  COMMERCIAL_REFINANCE_BORROWER_RETIREMENT,
  COMMERCIAL_REFINANCE_BORROWER_QUESTIONS,
  COMMERCIAL_REFINANCE_FINAL,
  COMMERCIAL_REFINANCE_PERSONAL_INFO,
  COMMERCIAL_REFINANCE_PROPERTY_DETAILS,
  COMMERCIAL_REFINANCE_BUSINESS_MANAGEMENT,
  COMMERCIAL_REFINANCE_BUSINESS_FINANCIALS,
  COMMERCIAL_REFINANCE_BORROWER_RENTER_INFO,
  COMMERCIAL_REFINANCE_BORROWER_LIVING_SITUATION,
  COMMERCIAL_REFINANCE_BUSINESS_OWNERS,
  COMMERCIAL_REFINANCE_EXISTING_CUSTOMER,
  COMMERCIAL_REFINANCE_EXISTING_CUSTOMER_INFO,
} from './constants';
import { handleCommercialBackButton, handleCommercialFormSubmit } from 'app/actions/form/actions';
import { getCommercialBridgeLoanQuestionsByName } from '../questions/commercial-bridge-loan';
import { BorrowerType } from '@lenderful/domain';
import { ajaxValidateBorrowerResidence } from 'app/actions/form/address-validation/actions';
import { Disclosure } from 'app/components/Disclaimer/Disclosure';

export const getCommercialRefinanceSectionById = (id: number): Section => {
  return COMMERCIAL_REFINANCE_SECTIONS.find((section) => section.id === id) as Section;
};

const COMMERCIAL_REFINANCE_SECTIONS: Section[] = [
  {
    id       : COMMERCIAL_REFINANCE_LOAN_PURPOSE,
    questions: commercialRefinanceQuestionsByName([
      FieldNames.loanPurpose,
    ]),
    agreement: <FormTerms />,
    hideBackButton: handleCommercialBackButton,
  },
  {
    id       : COMMERCIAL_REFINANCE_PROPERTY_INFO,
    title    : 'Where is the location of the property you are refinancing?',
    questions: commercialRefinanceQuestionsByName([
      FieldNames.propertyStreet,
      FieldNames.propertyStreet2,
      FieldNames.propertyCity,
      FieldNames.propertyState,
      FieldNames.propertyZip,
    ]),
  },
  {
    id       : COMMERCIAL_REFINANCE_PROPERTY_COST,
    title    : 'Tell us more about the property you are refinancing',
    questions: commercialRefinanceQuestionsByName([
      FieldNames.commercialPropertyType,
      FieldNames.propertyValue,
      FieldNames.commercialLienBalance,
      FieldNames.loanAmount,
      FieldNames.yearTaxes,
      FieldNames.yearInsure,
    ]),
  },
  {
    id        : COMMERCIAL_REFINANCE_PROPERTY_DETAILS,
    title     : 'Tell us more about your refinance',
    questions : commercialRefinanceQuestionsByName([
      FieldNames.landSize,
      FieldNames.numOfStructures,
      FieldNames.buildingSquareFootage,
      FieldNames.builtYear,
      FieldNames.expectedOwnerOccupancy,
      FieldNames.currentZoningClassification,
      FieldNames.buildingContactName,
      FieldNames.buildingContactPhone,
    ]),
  },
  {
    id             : COMMERCIAL_REFINANCE_EXISTING_CUSTOMER,
    questions      : commercialRefinanceQuestionsByName([
      FieldNames.isCommercialExistingCustomer,
    ]),
  },
  {
    id        : COMMERCIAL_REFINANCE_BUSINESS_INFO,
    title     : 'Tell us about your business',
    questions : commercialRefinanceQuestionsByName([
      FieldNames.commercialBusinessName,
      FieldNames.commercialSameBusiness,
      FieldNames.commercialStreet,
      FieldNames.commercialStreet2,
      FieldNames.commercialCity,
      FieldNames.commercialState,
      FieldNames.commercialZip,
      FieldNames.commercialPhone,
      FieldNames.commercialDBA,
      FieldNames.commercialCensusTract,
      FieldNames.commercialNAICSCode,
      FieldNames.commercialEmployeeCount,
      FieldNames.commercialWebsite,
      FieldNames.commercialEntityType,
      FieldNames.commercialBusinessType,
      FieldNames.commercialBusinessTypeOther,
      FieldNames.commercialStateOfIncorporation,
      FieldNames.commercialYearStarted,
    ]),
    showIf: [(values) => !isExistingCommercial(values)],
  },
  {
    id        : COMMERCIAL_REFINANCE_BUSINESS_FINANCIALS,
    title     : 'Tell us about your business financials',
    questions : commercialRefinanceQuestionsByName([
      FieldNames.commercialRevenueTrend3,
      FieldNames.commercialRevenueLastYear,
      FieldNames.commercialRevenueExplanation,
      FieldNames.commercialProfitTrend3,
      FieldNames.commercialProfitExplanation,
      FieldNames.commercialCollateralList,
      FieldNames.commercialBusinessOwnershipType,
    ]),
    showIf: [(values) => !isExistingCommercial(values)],
  },
  {
    id        : COMMERCIAL_REFINANCE_BUSINESS_OWNERS,
    questions : commercialRefinanceQuestionsByName([
      FieldNames.commercialOwnerList,
    ]),
    showIf: [(values) => !isExistingCommercial(values)],
  },
  {
    id        : COMMERCIAL_REFINANCE_BUSINESS_MANAGEMENT,
    questions : commercialRefinanceQuestionsByName([
      FieldNames.hasOtherManagement,
      FieldNames.commercialManagementList,
    ]),
    showIf: [(values) => !isExistingCommercial(values)],
  },
  {
    id       : COMMERCIAL_REFINANCE_LOAN_HOLDER,
    questions: commercialRefinanceQuestionsByName([
      FieldNames.commercialLoanHolder,
    ]),
    showIf: [(values) => !isExistingCommercial(values)],
  },
  {
    id       : COMMERCIAL_REFINANCE_PERSONAL_INFO,
    title    : 'Enter your personal information',
    questions: commercialRefinanceQuestionsByName([
      FieldNames.firstName,
      FieldNames.middleName,
      FieldNames.lastName,
      FieldNames.suffixName,
      FieldNames.email,
      FieldNames.phone,
      FieldNames.dateOfBirth,
    ]),
    showIf: [(values) => !isExistingCommercial(values)],
  },
  {
    id        : COMMERCIAL_REFINANCE_BORROWER_LIVING_SITUATION,
    title     : 'What is your current housing situation?',
    questions : commercialRefinanceQuestionsByName([
      FieldNames.livingRentOrOwn,
    ]),
    showIf: [(values) => !isExistingCommercial(values)],
  },
  {
    id       : COMMERCIAL_REFINANCE_HOUSING_INFO,
    title    : 'Provide your homeowner information',
    questions: commercialRefinanceQuestionsByName([
      FieldNames.livingPropertyType,
      FieldNames.livingPropertyStreet,
      FieldNames.livingPropertyStreet2,
      FieldNames.livingPropertyCity,
      FieldNames.livingPropertyState,
      FieldNames.livingPropertyZip,
    ]),
    showIf: [(values) => isHomeowner(values) && !isExistingCommercial(values)],
    submitAction : (formValues) => ajaxValidateBorrowerResidence(formValues),
  },
  {
    id        : COMMERCIAL_REFINANCE_BORROWER_RENTER_INFO,
    title     : 'Provide your renter information',
    questions : commercialRefinanceQuestionsByName([
      FieldNames.livingPropertyStreet,
      FieldNames.livingPropertyStreet2,
      FieldNames.livingPropertyCity,
      FieldNames.livingPropertyState,
      FieldNames.livingPropertyZip,
      FieldNames.livingMonthlyRentAmount,
    ]),
    showIf : [(values) => isRenting(values) && !isExistingCommercial(values)],
    submitAction : (formValues) => ajaxValidateBorrowerResidence(formValues),
  },
  {
    id       : COMMERCIAL_REFINANCE_BORROWER_INCOME_SOURCE,
    title    : 'What is your income source?',
    questions: commercialRefinanceQuestionsByName([
      FieldNames.employmentType,
    ]),
    showIf: [(values) => !isExistingCommercial(values)],
  },
  {
    id       : COMMERCIAL_REFINANCE_BORROWER_EMPLOYMENT,
    title    : 'Tell us about your income and employment',
    questions: commercialRefinanceQuestionsByName([
      FieldNames.commercialEmploySameBusiness,
      FieldNames.employCompanyName,
      FieldNames.employStreet,
      FieldNames.employStreet2,
      FieldNames.employCity,
      FieldNames.employState,
      FieldNames.employZip,
      FieldNames.employTitle,
      FieldNames.employPhone,
      FieldNames.employStart,
      FieldNames.employBase,
      FieldNames.employOvertime,
      FieldNames.employCommission,
      FieldNames.employBonus,
      FieldNames.additionalIncome,
      FieldNames.employPrevName,
      FieldNames.employPrevStreet,
      FieldNames.employPrevStreet2,
      FieldNames.employPrevCity,
      FieldNames.employPrevState,
      FieldNames.employPrevZip,
      FieldNames.employPrevTitle,
      FieldNames.employPrevStart,
      FieldNames.employPrevEnd,
    ]),
    showIf: [(values) => (isEmployee(values) || isSelfEmployed(values)) && !isExistingCommercial(values)],
  },
  {
    id       : COMMERCIAL_REFINANCE_BORROWER_RETIREMENT,
    questions: commercialRefinanceQuestionsByName([
      FieldNames.retirementIncome,
      FieldNames.otherIncome,
    ]),
    showIf: [(values) => (isRetired(values) ||  isEmploymentOther(values)) && !isExistingCommercial(values)],
  },
  {
    id           : COMMERCIAL_REFINANCE_EXISTING_CUSTOMER_INFO,
    title        : 'Please enter your Loan and Business Information',
    questions    : getCommercialBridgeLoanQuestionsByName([
      FieldNames.existingLoanNumber,
      FieldNames.commercialBusinessName,
      FieldNames.commercialStreet,
      FieldNames.commercialStreet2,
      FieldNames.commercialCity,
      FieldNames.commercialState,
      FieldNames.commercialZip,
      FieldNames.firstName,
      FieldNames.lastName,
      FieldNames.email,
      FieldNames.phone,
    ]),
    showIf       : [isExistingCommercial],
    submitAction : handleCommercialFormSubmit,
    submitText   : 'Submit',
  },
  {
    id           : COMMERCIAL_REFINANCE_BORROWER_QUESTIONS,
    title        : 'Borrower Questions',
    questions    : getCommercialDeclarationQuestions(BorrowerType.PRIMARY),
    showIf       : [(values) => !isExistingCommercial(values)],
    submitAction : handleCommercialFormSubmit,
    submitText   : 'Submit',
    agreement    : <Disclosure />,
  },
  {
    id              : COMMERCIAL_REFINANCE_FINAL,
    ifNoQuestions   : ConfirmationCommercial,
    hideProgressBar : true,
  },
];
